// Inlined to avoid pulling @ziggapp/klap-core (and the editor/player chunk it
// transitively imports) into the home page bundle. Mirrors
// klap.shared.constants.time.MICRO_SECONDS_IN_SECOND.
const MICRO_SECONDS_IN_SECOND = 1_000_000;

export function convertMicrosecondsToMinSec(microseconds: number) {
  if (!Number.isFinite(microseconds)) {
    return "0:00";
  }
  if (microseconds < 0) {
    microseconds = 0;
  }
  var seconds = Math.floor(microseconds / MICRO_SECONDS_IN_SECOND);
  var hours = Math.floor(seconds / 3600);
  var minutes = Math.floor((seconds % 3600) / 60);
  var sec = Math.floor(seconds % 60);

  if (sec === 60) {
    minutes++;
    sec = 0;
  }

  if (minutes === 60) {
    hours++;
    minutes = 0;
  }

  var result = "";

  if (hours > 0) {
    result += hours + ":";
  }

  var minuteString =
    hours > 0 ? (minutes < 10 ? "0" : "") + minutes : minutes.toString();

  result += minuteString + ":" + (sec < 10 ? "0" : "") + sec;

  return result;
}

// Formats a [start, end] span as three labels that always agree: each endpoint
// is floored to whole seconds, and the duration is the difference of those
// floored seconds — NOT floor(end - start). Flooring the endpoints
// independently and also flooring the raw difference can disagree by a second
// (e.g. "15:40 to 20:37" reads as 4:57 while floor(end - start) is 4:56), so we
// derive the duration from the same seconds we display. `durationUs` is the
// matching microsecond value for any duration readout that formats internally.
export function formatTimeRange(startUs: number, endUs: number) {
  const startSec = Math.max(0, Math.floor(startUs / MICRO_SECONDS_IN_SECOND));
  const endSec = Math.max(0, Math.floor(endUs / MICRO_SECONDS_IN_SECOND));
  const durationSec = Math.max(0, endSec - startSec);
  return {
    start: convertMicrosecondsToMinSec(startSec * MICRO_SECONDS_IN_SECOND),
    end: convertMicrosecondsToMinSec(endSec * MICRO_SECONDS_IN_SECOND),
    duration: convertMicrosecondsToMinSec(durationSec * MICRO_SECONDS_IN_SECOND),
    durationUs: durationSec * MICRO_SECONDS_IN_SECOND,
  };
}

export function convertMicrosecondsToMinLiteralSec(
  microseconds: number
): string {
  let seconds = microseconds / MICRO_SECONDS_IN_SECOND;
  let minutes = Math.floor(seconds / 60);
  let sec = seconds % 60;

  if (sec === 60) {
    minutes++;
    sec = 0;
  }

  if (minutes === 60) {
    minutes = 0;
  }

  let result = "";

  if (minutes > 0) {
    result += `${minutes}min`;
  } else {
    result += `${sec.toFixed()}s`;
  }

  return result;
}

export function convertMillisecondsToMinSec(milliseconds: number) {
  var seconds = Math.floor(milliseconds / 1000);
  var hours = Math.floor(seconds / 3600);
  var minutes = Math.floor((seconds % 3600) / 60);
  var sec = Math.floor(seconds % 60);

  if (sec === 60) {
    minutes++;
    sec = 0;
  }

  if (minutes === 60) {
    hours++;
    minutes = 0;
  }

  var result = "";

  if (hours > 0) {
    result += (hours < 10 ? "0" : "") + hours + ":";
  }

  var minuteString =
    hours > 0 ? (minutes < 10 ? "0" : "") + minutes : minutes.toString();

  result += minuteString + ":" + (sec < 10 ? "0" : "") + sec;

  return result;
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function convertHTMLTagsToEntities(input: string): string {
  // Using a regular expression to match any HTML tags
  const regex = /<[^>]*>/g;

  // Replacing each HTML tag with its corresponding HTML entities
  const result = input.replace(regex, (match) => {
    return match
      .split("")
      .map((char) => {
        switch (char) {
          case "<":
            return "&lt;";
          case ">":
            return "&gt;";
          case "&":
            return "&amp;";
          case '"':
            return "&quot;";
          case "'":
            return "&#39;";
          default:
            return char;
        }
      })
      .join("");
  });

  return result;
}

export const printTimeLiteral = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const sec = Math.round(seconds % 60);

  if (hours > 0) {
    const hourText = `${hours} hour${hours > 1 ? "s" : ""}`;
    if (minutes > 0) {
      const minuteText = `${minutes} minute${minutes > 1 ? "s" : ""}`;
      return `${hourText} ${minuteText}`;
    }
    return hourText;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  } else {
    return `${sec}s`;
  }
};

export function extractMDTitle(markdown: string): string | null {
  const titleRegex = /#\s*(.+)/;
  const match = markdown.match(titleRegex);

  return match ? match[1].trim() : null;
}

export function removeMDTitle(markdown: string): string {
  const titleRegex = /#\s*(.+)/;
  return markdown.replace(titleRegex, "");
}

// Note: `formatNumber` lives in `utils/format-number.ts`. It used to be
// duplicated here; the duplicate was removed to avoid drift. Import from
// `../utils/format-number` instead.
